import React, { useRef, useState, useEffect } from "react";
import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import { Link } from "react-router-dom";
import { PROFILE } from "../data/content";

/**
 * Dynamic AI logo with mouse physics.
 *  - Magnetic attraction toward cursor
 *  - Per-letter scatter / weight-morph on the name
 *  - Fluid tracking on "UX Director"
 *  - Kinetic light-source shadow cast opposite the cursor
 *  - Disabled on touch / coarse pointers (crisp static fallback)
 */
const NAME = PROFILE.name.split("");

export const DynamicLogo = ({ size = "md" }) => {
  const wrapRef = useRef(null);
  const [isTouch, setIsTouch] = useState(false);
  const [hoverName, setHoverName] = useState(false);
  const [pointer, setPointer] = useState({ x: 0, y: 0, inside: false });

  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const sx = useSpring(mx, { stiffness: 150, damping: 15, mass: 0.4 });
  const sy = useSpring(my, { stiffness: 150, damping: 15, mass: 0.4 });

  // magnetic translate for whole block
  const tx = useTransform(sx, (v) => v * 0.18);
  const ty = useTransform(sy, (v) => v * 0.18);
  // kinetic shadow (opposite direction)
  const shadow = useTransform([sx, sy], ([x, y]) =>
    `${(-x * 0.12).toFixed(1)}px ${(-y * 0.12).toFixed(1)}px 24px var(--accent-soft)`
  );

  useEffect(() => {
    setIsTouch(window.matchMedia("(pointer: coarse)").matches);
  }, []);

  const handleMove = (e) => {
    if (isTouch || !wrapRef.current) return;
    const r = wrapRef.current.getBoundingClientRect();
    const cx = r.left + r.width / 2;
    const cy = r.top + r.height / 2;
    const dx = e.clientX - cx;
    const dy = e.clientY - cy;
    const dist = Math.hypot(dx, dy);
    // magnetic radius ~ 90px
    if (dist < 120) {
      mx.set(dx);
      my.set(dy);
    } else {
      mx.set(0);
      my.set(0);
    }
    setPointer({ x: e.clientX - r.left, y: e.clientY - r.top, inside: true });
  };

  const handleLeave = () => {
    mx.set(0);
    my.set(0);
    setPointer((p) => ({ ...p, inside: false }));
    setHoverName(false);
  };

  const isLight = typeof document !== "undefined" && document.documentElement.getAttribute("data-mode") === "light";

  const sizes = {
    md: { name: "text-2xl sm:text-[1.7rem]", role: "text-[0.6rem]" },
    lg: { name: "text-5xl sm:text-7xl", role: "text-xs sm:text-sm" },
  }[size];

  return (
    <Link to="/" data-testid="logo-home-link" aria-label="Yuvakumar — Home" className="inline-block">
      <motion.div
        ref={wrapRef}
        onMouseMove={handleMove}
        onMouseLeave={handleLeave}
        style={{ x: isTouch ? 0 : tx, y: isTouch ? 0 : ty }}
        className="relative select-none leading-none"
        data-testid="dynamic-logo"
      >
        <motion.div style={{ textShadow: isTouch ? "none" : shadow }} className="flex flex-col">
          <span
            className={`display font-bold ${sizes.name} tracking-tight`}
            style={{ color: "var(--text)" }}
            onMouseEnter={() => setHoverName(true)}
            onMouseLeave={() => setHoverName(false)}
          >
            {NAME.map((ch, i) => {
              // per-letter displacement based on pointer proximity along x
              let dyChar = 0;
              let weight = 400;
              if (!isTouch && hoverName && pointer.inside && wrapRef.current) {
                const approx = (i / NAME.length) * wrapRef.current.offsetWidth;
                const d = Math.abs(approx - pointer.x);
                const influence = Math.max(0, 1 - d / 90);
                dyChar = -influence * 10;
                weight = 400 + influence * 500;
              }
              return (
                <motion.span
                  key={i}
                  animate={{ y: dyChar, fontWeight: weight }}
                  transition={{ type: "spring", stiffness: 260, damping: 18 }}
                  className="inline-block"
                  style={{ willChange: "transform" }}
                >
                  {ch}
                </motion.span>
              );
            })}
          </span>
        </motion.div>

        {/* accent underline */}
        <span
          aria-hidden
          className="block mt-1 h-px origin-left"
          style={{
            background: "var(--accent)",
            transform: pointer.inside && !isTouch ? "scaleX(1)" : "scaleX(0.35)",
            transition: "transform 0.4s cubic-bezier(0.16,1,0.3,1)",
          }}
        />
      </motion.div>
    </Link>
  );
};

export default DynamicLogo;
